(function () {
  const REQUEST_TIMEOUT_MS = 20000;
  const CART_WAIT_TIMEOUT_MS = 20000;
  let validationPromise;
  let validationQueued = false;
  let cartUpdatePromise = Promise.resolve();
  let finishCartUpdate;
  let checkoutLock;

  function delay(ms) {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
  }

  async function fetchWithTimeout(url, options = {}) {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

    try {
      return await fetch(url, {
        ...options,
        signal: controller.signal,
      });
    } finally {
      window.clearTimeout(timeout);
    }
  }

  function disableCheckout() {
    document.querySelectorAll('[name="checkout"]').forEach((button) => {
      if (!button.disabled) {
        button.dataset.restrictedKitCheckoutLocked = "true";
      }
      button.disabled = true;
    });
  }

  function enableCheckout() {
    document.querySelectorAll('[data-restricted-kit-checkout-locked="true"]').forEach((button) => {
      delete button.dataset.restrictedKitCheckoutLocked;
      button.disabled = false;
    });
  }

  function hasRestrictedKitInCart() {
    return !!document.querySelector("[data-restricted-kit-cart]");
  }

  function isRestrictedKitQuantityButton(target) {
    return !!target.closest?.(
      "[data-restricted-kit-cart] [data-increase-quantity], " +
      "[data-restricted-kit-cart] [data-decrease-quantity]"
    );
  }

  function startCartUpdate() {
    if (finishCartUpdate) return;

    cartUpdatePromise = new Promise((resolve) => {
      finishCartUpdate = resolve;
    });
    disableCheckout();
    checkoutLock = window.setInterval(disableCheckout, 50);
  }

  function endCartUpdate() {
    if (!finishCartUpdate) return;

    window.clearInterval(checkoutLock);
    checkoutLock = null;
    enableCheckout();
    finishCartUpdate?.();
    finishCartUpdate = null;
  }

  async function waitForCartTransform() {
    const startedAt = Date.now();
    while (
      sessionStorage.getItem("TRANSFORMING_CART") === "true" &&
      Date.now() - startedAt < CART_WAIT_TIMEOUT_MS
    ) {
      await delay(50);
    }
  }

  async function runValidation() {
    const elements = document.querySelectorAll("[data-restricted-kit]");
    if (!elements.length) return true;

    if (!CUSTOMER_ID) {
      elements.forEach((element) => {
        if (!element.hasAttribute("data-restricted-kit-cart")) {
          element.hidden = true;
        }
      });
      return true;
    }

    const cartResponse = await fetchWithTimeout("/cart.js");
    if (!cartResponse.ok) throw new Error(`HTTP ${cartResponse.status}`);
    let cart = await cartResponse.json();

    const skus = new Set();
    elements.forEach((element) => {
      if (element.dataset.restrictedKitSku) {
        skus.add(element.dataset.restrictedKitSku);
      }
    });

    const url = `${window.BACKEND_BASE_URL.replace(/\/$/, "")}/kits/validate`;
    const validations = new Map();

    for (const sku of skus) {
      const response = await fetchWithTimeout(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ customerId: CUSTOMER_ID, sku }),
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      validations.set(sku, data.results);
    }

    const updates = {};

    for (const [sku, validation] of validations) {
      let remaining = Number(validation.allowedQuantity) || 0;
      for (const item of cart.items.filter((cartItem) => cartItem.sku === sku)) {
        const quantity = Math.min(item.quantity, remaining);
        if (quantity !== item.quantity) {
          updates[item.key] = quantity;
        }
        remaining -= quantity;
      }
    }

    if (Object.keys(updates).length) {
      const response = await fetchWithTimeout("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ updates }),
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      cart = await response.json();
    }

    elements.forEach((element) => {
      const sku = element.dataset.restrictedKitSku;
      const validation = validations.get(sku);
      const button = element.querySelector("[data-add-to-cart]");
      const input = element.querySelector("[data-quantity-input]");
      const increaseButton = element.querySelector("[data-increase-quantity]");
      const isCartItem = element.hasAttribute("data-restricted-kit-cart");

      if (!validation) return;

      const allowedQuantity = Number(validation.allowedQuantity) || 0;
      const quantityInCart = cart.items
        .filter((item) => item.sku === sku)
        .reduce((total, item) => total + item.quantity, 0);
      const cartItem = cart.items.find(
        (item) => item.key === element.dataset.restrictedKitKey
      );
      const availableQuantity = isCartItem
        ? Math.max(allowedQuantity - quantityInCart + (cartItem?.quantity || 0), 0)
        : Math.max(allowedQuantity - quantityInCart, 0);

      if (input) input.max = availableQuantity;
      if (increaseButton && isCartItem) {
        increaseButton.disabled = Number(input?.value) >= availableQuantity;
      }
      if (button) {
        const productAvailable = element.dataset.restrictedKitAvailable === "true";
        delete button.dataset.restrictedKitAdding;
        button.disabled = !productAvailable || availableQuantity < 1;
      }
      if (!isCartItem) {
        element.hidden = availableQuantity < 1;
      }
    });

    if (Object.keys(updates).length) {
      document.dispatchEvent(new CustomEvent("theme:cart:change", {
        detail: { cart },
        bubbles: true,
      }));
      document.dispatchEvent(new CustomEvent("theme:cart:reload", { bubbles: true }));
    }

    return true;
  }

  function validateRestrictedKits() {
    if (validationPromise) {
      validationQueued = true;
      return validationPromise;
    }

    validationPromise = (async () => {
      let valid = true;
      do {
        validationQueued = false;
        valid = await runValidation();
      } while (validationQueued);
      return valid;
    })()
      .catch((error) => {
        console.error("Error validating restricted kits:", error);
        return false;
      })
      .finally(() => {
        validationPromise = null;
      });

    return validationPromise;
  }

  async function validateBeforeCheckout() {
    await Promise.race([cartUpdatePromise, delay(CART_WAIT_TIMEOUT_MS)]);
    if (validationPromise) {
      await validationPromise;
    }
    return validateRestrictedKits();
  }

  document.addEventListener("DOMContentLoaded", validateRestrictedKits);
  document.addEventListener("theme:cart:reload", validateRestrictedKits);
  document.addEventListener("theme:cart:change", async () => {
    await validateRestrictedKits();
    await waitForCartTransform();
    endCartUpdate();
  });
  document.addEventListener("swym:cart-loaded", validateRestrictedKits);

  document.addEventListener("click", (event) => {
    if (isRestrictedKitQuantityButton(event.target)) startCartUpdate();
  }, true);

  document.addEventListener("change", (event) => {
    if (event.target.matches("[data-restricted-kit-cart] [data-quantity-input]")) {
      startCartUpdate();
    }
  }, true);

  document.addEventListener("click", (event) => {
    const button = event.target.closest("[data-add-to-cart]");
    const element = button?.closest("[data-restricted-kit]");
    if (!button || !element || element.hasAttribute("data-restricted-kit-cart")) return;

    if (button.dataset.restrictedKitAdding === "true") {
      event.preventDefault();
      event.stopImmediatePropagation();
      button.disabled = true;
      return;
    }

    button.dataset.restrictedKitAdding = "true";
    button.disabled = true;
  }, true);

  document.addEventListener("click", async (event) => {
    const button = event.target.closest('[name="checkout"]');
    if (!button) return;
    if (button.dataset.restrictedKitValidated === "true") {
      delete button.dataset.restrictedKitValidated;
      return;
    }
    if (!hasRestrictedKitInCart()) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    button.disabled = true;

    if (await validateBeforeCheckout()) {
      button.disabled = false;
      button.dataset.restrictedKitValidated = "true";
      button.click();
    } else {
      button.disabled = false;
    }
  }, true);

  document.addEventListener("submit", async (event) => {
    const form = event.target;
    const isCheckoutSubmit =
      event.submitter?.name === "checkout" ||
      !!form.querySelector?.('input[name="checkout"]');

    if (!isCheckoutSubmit) return;
    if (form.dataset.restrictedKitValidated === "true") {
      delete form.dataset.restrictedKitValidated;
      return;
    }
    if (!hasRestrictedKitInCart()) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (await validateBeforeCheckout()) {
      form.dataset.restrictedKitValidated = "true";
      if (event.submitter && typeof form.requestSubmit === "function") {
        form.requestSubmit(event.submitter);
      } else if (typeof form.requestSubmit === "function") {
        form.requestSubmit();
      }
    }
  }, true);

  document.addEventListener("theme:variant:change", (event) => {
    const element = event.target.querySelector?.("[data-restricted-kit]");
    const variant = event.detail?.variant;
    if (!element || !variant) return;

    element.dataset.restrictedKitSku = variant.sku;
    element.dataset.restrictedKitAvailable = variant.available;
    element.hidden = true;
    const button = element.querySelector("[data-add-to-cart]");
    if (button) button.disabled = true;
    validateRestrictedKits();
  });
})();
